import React from "react";

/**
 * Shared UI primitives — modernized "Refresh" pass.
 * Mobile (PWA): rounded, airy, touch-first.
 * Desktop: denser, sharper edges, more refined typography.
 * Brand: sage/gold/ink palette + Playfair Display / IBM Plex Sans.
 */

export function PageHeader({ eyebrow, title, description, actions, testId }) {
  return (
    <div
      className="flex flex-col gap-3 sm:gap-4 lg:flex-row lg:items-end lg:justify-between lg:gap-6 mb-5 sm:mb-7 lg:mb-9 lg:pb-5 lg:border-b lg:border-border/70"
      data-testid={testId}
    >
      <div className="min-w-0">
        {eyebrow && (
          <div className="hidden lg:flex items-center gap-2 text-[10px] uppercase tracking-[0.32em] text-primary mb-2.5">
            <span className="inline-block h-px w-6 bg-bansal-gold/70" />
            {eyebrow}
          </div>
        )}
        <h1 className="hidden lg:block font-serif text-3xl xl:text-[2.35rem] text-foreground leading-[1.08] tracking-tight break-words">
          {title}
        </h1>
        {description && (
          <p className="text-sm text-muted-foreground lg:mt-3 max-w-2xl leading-relaxed">{description}</p>
        )}
      </div>
      {actions && (
        <div className="flex flex-col sm:flex-row sm:items-center gap-2 shrink-0 w-full lg:w-auto [&_button]:min-h-11 lg:[&_button]:min-h-9">
          {actions}
        </div>
      )}
    </div>
  );
}

export function KpiTile({ label, value, accent, sub, testId, icon: Icon }) {
  return (
    <div
      className="group relative border border-border rounded-2xl lg:rounded-lg bg-card p-4 sm:p-5 pwa-card transition-transform duration-200 active:scale-[0.98] lg:hover:-translate-y-0.5 lg:hover:shadow-[0_8px_24px_-12px_rgba(85,107,47,0.18)] overflow-hidden"
      data-testid={testId}
    >
      {accent && (
        <span
          aria-hidden
          className="absolute inset-y-0 left-0 w-[3px] bg-gradient-to-b from-bansal-sage to-bansal-gold opacity-80"
        />
      )}
      <div className="flex items-start justify-between gap-2">
        <div className="text-[10px] uppercase tracking-[0.28em] text-muted-foreground">{label}</div>
        {Icon && <Icon className="h-4 w-4 text-primary/70 shrink-0" aria-hidden />}
      </div>
      <div className={`font-serif text-[1.75rem] sm:text-3xl mt-2.5 sm:mt-3 tabular leading-tight break-words ${accent ? "text-primary" : "text-foreground"}`}>
        {value}
      </div>
      {sub && <div className="text-xs text-muted-foreground mt-1">{sub}</div>}
    </div>
  );
}

export function SectionCard({ title, children, action, testId, className = "", subtitle }) {
  return (
    <div
      className={`border border-border rounded-2xl lg:rounded-lg bg-card pwa-card overflow-hidden min-w-0 transition-shadow duration-200 lg:hover:shadow-[0_4px_18px_-12px_rgba(26,26,26,0.18)] ${className}`}
      data-testid={testId}
    >
      <div className="px-4 py-3.5 sm:px-5 sm:py-4 border-b border-border/80 bg-gradient-to-b from-muted/40 to-transparent flex flex-col gap-2 sm:flex-row sm:items-start sm:justify-between min-w-0">
        <div className="min-w-0">
          <h3 className="font-sans text-sm sm:text-[15px] font-semibold tracking-tight text-foreground min-w-0 break-words">
            {title}
          </h3>
          {subtitle && <p className="text-xs text-muted-foreground mt-1 leading-relaxed">{subtitle}</p>}
        </div>
        {action && <div className="shrink-0 flex flex-wrap items-center gap-2 max-w-full">{action}</div>}
      </div>
      <div className="p-4 sm:p-5">{children}</div>
    </div>
  );
}

export function Surface({ children, className = "", testId, tone = "default" }) {
  const tones = {
    default: "bg-card border-border",
    muted: "bg-muted/40 border-border/70",
    sage: "bg-bansal-sageLight/40 border-bansal-sage/30 dark:bg-bansal-sage/10",
    gold: "bg-bansal-goldLight/35 border-bansal-gold/30 dark:bg-bansal-gold/10",
  };
  return (
    <div
      className={`border rounded-xl lg:rounded-md p-4 sm:p-5 min-w-0 ${tones[tone]} ${className}`}
      data-testid={testId}
    >
      {children}
    </div>
  );
}

export function EmptyState({ icon: Icon, title, description, action, testId }) {
  return (
    <div
      className="text-center py-10 sm:py-14 px-4 rounded-xl border border-dashed border-border bg-muted/30"
      data-testid={testId}
    >
      {Icon && (
        <div className="mx-auto mb-3 h-11 w-11 rounded-full bg-bansal-sageLight/70 dark:bg-bansal-sage/15 grid place-items-center text-primary">
          <Icon className="h-5 w-5" aria-hidden />
        </div>
      )}
      {title && <p className="font-serif text-lg text-foreground">{title}</p>}
      {description && <p className="text-sm text-muted-foreground mt-1.5 max-w-md mx-auto leading-relaxed">{description}</p>}
      {action && <div className="mt-4 flex justify-center">{action}</div>}
    </div>
  );
}

export function Toolbar({ children, className = "" }) {
  return (
    <div
      className={`flex flex-wrap items-center gap-2 rounded-xl lg:rounded-md border border-border/70 bg-muted/40 px-3 py-2 ${className}`}
    >
      {children}
    </div>
  );
}

export function Pill({ children, tone = "default", className = "" }) {
  const tones = {
    default: "bg-muted text-muted-foreground border border-border",
    success: "bg-emerald-500/15 text-emerald-800 dark:text-emerald-300 border border-emerald-500/25",
    warning: "bg-amber-500/15 text-amber-900 dark:text-amber-300 border border-amber-500/25",
    danger: "bg-red-500/15 text-red-700 dark:text-red-300 border border-red-500/25",
    sage: "bg-primary/15 text-primary dark:text-primary border border-primary/30",
    gold: "bg-bansal-gold/15 text-bansal-goldDark dark:text-bansal-gold border border-bansal-gold/35",
    ink: "bg-foreground text-background border border-foreground/20",
    info: "bg-sky-500/15 text-sky-800 dark:text-sky-300 border border-sky-500/25",
  };
  return (
    <span
      className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] uppercase tracking-[0.14em] font-medium leading-none whitespace-nowrap ${tones[tone] || tones.default} ${className}`}
    >
      {children}
    </span>
  );
}

const STATE_TONE = {
  GATE_IN: "gold",
  WEIGH_GROSS: "sage",
  LAB_PENDING: "warning",
  BARGAINING: "warning",
  APPROVED: "success",
  UNLOADING: "sage",
  UNLOADING_HOLD: "warning",
  RELAB_PENDING: "warning",
  WEIGH_MID: "gold",
  WEIGH_TARE: "sage",
  ACCOUNTS_PENDING: "warning",
  COMPLETED: "success",
  REJECTED: "danger",
};

export function StatePill({ state }) {
  return <Pill tone={STATE_TONE[state] || "default"}>{state?.replaceAll("_", " ")}</Pill>;
}

/**
 * ModuleTabs — extensible tabbed workspace navigation.
 * layout="bar": horizontal pills on all breakpoints (Accounts head tabs).
 * layout="rail" (default): vertical mini-sidebar on desktop, horizontal on mobile.
 */
export function ModuleTabs({ items, value, onChange, testId, footer, layout = "rail" }) {
  const horizontal = layout === "bar";
  return (
    <>
      {/* Horizontal pill scroller (mobile, or bar layout on all breakpoints) */}
      <div
        className={`${horizontal ? "flex" : "lg:hidden flex"} -mx-1 px-1 gap-2 overflow-x-auto pb-1 snap-x snap-mandatory`}
        role="tablist"
        data-testid={testId ? `${testId}-mobile` : undefined}
      >
        {items.map((it) => {
          const active = it.value === value;
          return (
            <button
              key={it.value}
              type="button"
              role="tab"
              aria-selected={active}
              onClick={() => !it.disabled && onChange(it.value)}
              disabled={it.disabled}
              data-testid={testId ? `${testId}-${it.value}` : undefined}
              className={`snap-start shrink-0 flex items-center gap-2 px-3.5 py-2.5 rounded-full text-xs font-medium border min-h-11 transition-colors ${
                active
                  ? "bg-bansal-ink text-bansal-ivory border-bansal-ink shadow-sm dark:bg-bansal-sage dark:border-bansal-sage dark:text-white"
                  : it.preview
                    ? "bg-muted/60 text-foreground/70 border-dashed border-border active:bg-card"
                    : "bg-card text-foreground/75 border-border active:bg-muted/60"
              } ${it.disabled ? "opacity-40" : ""}`}
            >
              {it.icon && <it.icon className="h-4 w-4" />}
              <span className="whitespace-nowrap">{it.label}</span>
              {it.preview && !active && (
                <span className="text-[9px] uppercase tracking-[0.15em] rounded-full px-1.5 py-0.5 leading-none bg-bansal-gold/15 text-bansal-goldDark dark:text-bansal-gold border border-bansal-gold/30">
                  soon
                </span>
              )}
              {it.badge != null && (
                <span
                  className={`tabular text-[10px] rounded-full px-1.5 py-0.5 leading-none ${
                    active ? "bg-bansal-gold text-bansal-ink" : "bg-muted text-primary border border-border"
                  }`}
                >
                  {it.badge}
                </span>
              )}
            </button>
          );
        })}
      </div>

      {/* Desktop: vertical rail (hidden when bar layout) */}
      <nav
        className={`${horizontal ? "hidden" : "hidden lg:flex"} flex-col gap-1 bg-card border border-border rounded-lg p-2 sticky top-20 self-start`}
        role="tablist"
        aria-orientation="vertical"
        data-testid={testId}
      >
        {items.map((it) => {
          const active = it.value === value;
          return (
            <button
              key={it.value}
              type="button"
              role="tab"
              aria-selected={active}
              onClick={() => !it.disabled && onChange(it.value)}
              disabled={it.disabled}
              data-testid={testId ? `${testId}-${it.value}` : undefined}
              className={`group relative flex items-start gap-3 px-3 py-2.5 rounded-md text-left text-sm transition-colors ${
                active
                  ? "bg-bansal-sageLight/60 text-foreground dark:bg-bansal-sage/15"
                  : "text-foreground/70 hover:bg-muted/60 hover:text-foreground"
              } ${it.disabled ? "opacity-50 cursor-not-allowed" : ""}`}
            >
              <span
                aria-hidden
                className={`absolute left-0 top-2.5 bottom-2.5 w-[3px] rounded-r-full transition-colors ${
                  active ? "bg-bansal-gold" : "bg-transparent group-hover:bg-bansal-sage/40"
                }`}
              />
              {it.icon && <it.icon className="h-4 w-4 mt-0.5 shrink-0 opacity-80" />}
              <span className="min-w-0 flex-1">
                <span className="flex items-center gap-2 flex-wrap">
                  <span className="font-medium truncate">{it.label}</span>
                  {it.preview && (
                    <span className="text-[9px] uppercase tracking-[0.15em] rounded-full px-1.5 py-0.5 leading-none bg-bansal-gold/15 text-bansal-goldDark dark:text-bansal-gold border border-bansal-gold/30">
                      soon
                    </span>
                  )}
                  {it.badge != null && (
                    <span className="tabular text-[10px] rounded-full px-1.5 py-0.5 leading-none bg-muted border border-border text-primary">
                      {it.badge}
                    </span>
                  )}
                  {it.disabled && (
                    <span className="text-[9px] uppercase tracking-widest text-muted-foreground/70">soon</span>
                  )}
                </span>
                {it.hint && <span className="block text-[11px] text-muted-foreground mt-0.5">{it.hint}</span>}
              </span>
            </button>
          );
        })}
        {footer && <div className="pt-2 mt-1 border-t border-border/60">{footer}</div>}
      </nav>
    </>
  );
}
